import React from 'react';
import { useState, useEffect } from 'react';
import { IonPage, IonContent, IonHeader, IonToolbar, IonFooter } from '@ionic/react';
import { Capacitor } from '@capacitor/core';
import BottomBar from '@components/BottomBar.js';
import {
  ArrowLeft,
  Phone,
  MessageCircle,
  Pencil,
  Trash2,
  User,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Send,
  Copy,
  Check,
  Calendar,
  Clock,
  FileText,
  Paperclip,
  Building2,
  X,
  UserCheck,
  Users,
  Tag,
  Bell,
  Shield,
  ShieldCheck,
  Car,
} from 'lucide-react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { PolicyDocumentsSection } from '@features/policies/components/PolicyDocumentsSection.js';
import PolicyClaimsSection from '@features/claims/components/PolicyClaimsSection.js';
import {
  usePolicyQuery,
  useUpdatePolicyStatusMutation,
  useDeletePolicyMutation,
  usePolicyStatusHistoryQuery,
  policyService,
  useUpdatePolicyMutation,
} from '@features/policies/index.js';
import {
  formatDate,
  daysToExpiry,
  urgencyBucket,
  daysLabel,
  initials,
  isMotorPolicy,
} from '@repo/utils';
import {
  RENEWAL_STATUS_LABELS,
  URGENCY_LABELS,
  WHATSAPP_TEMPLATE,
  VALIDATION,
  VALIDATION_ERRORS,
} from '@repo/constants';
import { RenewalStatus } from '@repo/types';
import type { PolicyWithClient } from '@repo/types';
import PageLoader from '@components/ui/PageLoader.js';
import Dialog from '@components/ui/Dialog.js';
import AlertDialog from '@components/ui/AlertDialog.js';
import Button from '@components/ui/Button.js';
import Badge from '@components/ui/Badge.js';
import Input from '@components/ui/Input.js';
import { cn } from '@utils/Cn.js';
import { appConfig } from '@config/index.js';

const ALL_STATUSES: RenewalStatus[] = [
  RenewalStatus.PENDING,
  RenewalStatus.REMINDED,
  RenewalStatus.RENEWED,
  RenewalStatus.NOT_RENEWED,
  RenewalStatus.LAPSED,
  RenewalStatus.INACTIVE,
];

const statusMeta: Record<
  RenewalStatus,
  { dot: string; badge: 'pending' | 'reminded' | 'renewed' | 'notRenewed' | 'lapsed' | 'inactive' }
> = {
  [RenewalStatus.PENDING]: { dot: 'bg-amber-edge', badge: 'pending' },
  [RenewalStatus.REMINDED]: { dot: 'bg-sky-400', badge: 'reminded' },
  [RenewalStatus.RENEWED]: { dot: 'bg-green-edge', badge: 'renewed' },
  [RenewalStatus.NOT_RENEWED]: { dot: 'bg-red-edge', badge: 'notRenewed' },
  [RenewalStatus.LAPSED]: { dot: 'bg-gray-edge', badge: 'lapsed' },
  [RenewalStatus.INACTIVE]: { dot: 'bg-slate-400', badge: 'inactive' },
};

function resolveTemplate(
  template: string,
  vals: Record<string, string | number | boolean | null | undefined>,
): string {
  function s(v: string | number | boolean | null | undefined): string {
    if (v == null) return '';
    return String(v);
  }
  return template
    .replace(/\{\{insuredName\}\}/g, s(vals.insuredName))
    .replace(/\{\{endDate\}\}/g, s(vals.endDate))
    .replace(/\{\{policyType\}\}/g, s(vals.policyType))
    .replace(/\{\{vehicleNumber\}\}/g, s(vals.vehicleNumber))
    .replace(/\{\{policyNumber\}\}/g, s(vals.policyNumber))
    .replace(/\{\{premiumPrice\}\}/g, s(vals.premiumPrice))
    .replace(/\{\{previousClaim\}\}/g, s(vals.previousClaim))
    .replace(/\{\{paymentLink\}\}/g, s(vals.paymentLink))
    .replace(/\{\{additionalNotice\}\}/g, s(vals.additionalNotice))
    .replace(/\{\{baseUrl\}\}/g, s(vals.baseUrl))
    .replace(/\{\{policyId\}\}/g, s(vals.policyId))
    .replace(
      /\{\{#showVehicleNumber\}\}([\s\S]*?)\{\{\/showVehicleNumber\}\}/g,
      vals.showVehicleNumber ? '$1' : '',
    )
    .replace(/\{\{#paymentLink\}\}([\s\S]*?)\{\{\/paymentLink\}\}/g, vals.paymentLink ? '$1' : '')
    .replace(
      /\{\{#hasRenewalNoticePdf\}\}([\s\S]*?)\{\{\/hasRenewalNoticePdf\}\}/g,
      vals.hasRenewalNoticePdf ? '$1' : '',
    )
    .replace(
      /\{\{#additionalNotice\}\}([\s\S]*?)\{\{\/additionalNotice\}\}/g,
      vals.additionalNotice ? '$1' : '',
    )
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default function PolicyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { data, isLoading } = usePolicyQuery(id);
  const policy = data?.data;

  if (isLoading) return <PageLoader message="Loading policy detail cockpit" />;

  if (!policy) {
    return (
      <IonPage>
        <IonContent className="ion-padding-bottom">
          <div className="flex h-full flex-col items-center justify-center bg-body-bg px-4 text-center">
            <FileText size={48} className="text-ink-faint mb-4" />
            <h3 className="text-lg font-bold text-ink">Policy not found</h3>
            <p className="mt-1 text-sm text-ink-faint max-w-xs">
              This policy may have been deleted or the link is invalid.
            </p>
            <Button
              variant="outline"
              size="md"
              className="mt-6 flex items-center gap-2"
              onClick={() => {
                history.push('/policies');
              }}
            >
              <ArrowLeft size={15} />
              Back to policies
            </Button>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return <PolicyDetailContent policy={policy} id={id} />;
}

function PolicyDetailContent({ policy, id }: { policy: PolicyWithClient; id: string }) {
  const history = useHistory();
  const location = useLocation();
  const updateStatus = useUpdatePolicyStatusMutation();
  const deletePolicy = useDeletePolicyMutation();
  const updatePolicy = useUpdatePolicyMutation();
  const pdfInputRef = React.useRef<HTMLInputElement>(null);

  const [showWAModal, setShowWAModal] = useState(false);
  const [premiumPrice, setPremiumPrice] = useState('');
  const [paymentLink, setPaymentLink] = useState('');
  const [additionalNotice, setAdditionalNotice] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copied, setCopied] = useState(false);
  const [waErrors, setWaErrors] = useState<{ premiumPrice?: string; paymentLink?: string }>({});

  // Status Confirmation Modals State
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewLastRenewedDate, setRenewLastRenewedDate] = useState('');
  const [renewNextDueDate, setRenewNextDueDate] = useState('');
  const [renewPaidAmt, setRenewPaidAmt] = useState('');

  const [showReminderModal, setShowReminderModal] = useState(false);
  const [targetReminderStatus, setTargetReminderStatus] = useState<RenewalStatus | null>(null);
  const [nextReminderDate, setNextReminderDate] = useState('');

  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [targetArchiveStatus, setTargetArchiveStatus] = useState<RenewalStatus | null>(null);

  const { data: historyRes } = usePolicyStatusHistoryQuery(id);
  const historyData = historyRes?.data ?? [];

  function handleCopyLink() {
    if (!policy.paymentLink) return;
    void navigator.clipboard.writeText(policy.paymentLink);
    setCopied(true);
    toast.success('Payment link copied to clipboard');
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }

  async function handleViewPdf() {
    try {
      const blob = await policyService.getRenewalNoticePdf(policy.id);
      if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Filesystem')) {
        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const base64 = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => {
            if (typeof r.result === 'string') resolve(r.result.split(',')[1] ?? r.result);
            else reject(new Error('Failed to read blob'));
          };
          r.onerror = reject;
          r.readAsDataURL(blob);
        });
        const fileName = `renewal-notice-${policy.id}.pdf`;
        const saved = await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Cache,
        });

        // Try to open using FileOpener first, fallback to Share if unavailable or fails
        if (Capacitor.isPluginAvailable('FileOpener')) {
          try {
            const { FileOpener } = await import('@capacitor-community/file-opener');
            await FileOpener.open({
              filePath: saved.uri,
              contentType: 'application/pdf',
              openWithDefault: true,
            });
            return;
          } catch (openErr) {
            console.warn('FileOpener failed, falling back to Share:', openErr);
          }
        }

        if (Capacitor.isPluginAvailable('Share')) {
          const { Share } = await import('@capacitor/share');
          await Share.share({ title: 'Renewal Notice', files: [saved.uri] });
        } else {
          toast.error('Opening or sharing PDF is not supported on this device');
        }
      } else {
        const url = URL.createObjectURL(blob);
        const w = window.open(url, '_blank');
        if (!w) {
          const a = document.createElement('a');
          a.href = url;
          a.download = `renewal-notice-${policy.id}.pdf`;
          a.click();
        }
      }
    } catch (err) {
      console.error('handleViewPdf error:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load PDF');
    }
  }

  function handlePdfUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      updatePolicy.mutate(
        { id: policy.id, data: { renewalNotice: base64 } },
        {
          onSuccess: () => {
            toast.success('Renewal notice PDF uploaded successfully');
          },
        },
      );
    };
    reader.readAsDataURL(file);
  }

  function handleRemovePdf() {
    updatePolicy.mutate(
      { id: policy.id, data: { renewalNotice: '' } },
      {
        onSuccess: () => {
          toast.success('Renewal notice PDF removed successfully');
          if (pdfInputRef.current) {
            pdfInputRef.current.value = '';
          }
        },
      },
    );
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('wa') === 'true') {
      setPremiumPrice(String(policy.premiumPrice ?? ''));
      setPaymentLink(policy.paymentLink ?? '');
      setAdditionalNotice(policy.additionalNotice ?? '');
      setWaErrors({});
      setShowWAModal(true);
      history.replace(`/policies/${id}`);
    }
  }, [location.search, policy, id, history]);

  const days = daysToExpiry(policy.endDate);
  const urgency = urgencyBucket(days);

  const associateAgentObj = policy.associateAgent ?? policy.client.associateAgent;
  const isOutsourced = policy.isOutsourced || policy.client.isOutsourced;
  const tel =
    isOutsourced && associateAgentObj?.mobileNumber
      ? associateAgentObj.mobileNumber
      : policy.client.mobileNumber;

  function callAgent() {
    if (!tel) {
      toast.error('No phone number on file');
      return;
    }
    window.open(`tel:${tel}`);
  }

  function smsAgent() {
    if (!tel) {
      toast.error('No phone number on file');
      return;
    }
    const body = buildMessage();
    window.open(`sms:${tel}?body=${encodeURIComponent(body)}`, '_blank');
  }

  function buildMessage() {
    const rawPremium = premiumPrice || policy.premiumPrice;
    const formattedPremium = rawPremium
      ? `₹${Number(rawPremium).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '';
    return resolveTemplate(WHATSAPP_TEMPLATE, {
      insuredName: policy.insuredPersonName || policy.client.insuredName,
      policyType: policy.policyType.name,
      showVehicleNumber: isMotorPolicy(policy.policyType.name) && !!policy.vehicleNumber,
      vehicleNumber: policy.vehicleNumber,
      policyNumber: policy.policyNumber,
      endDate: formatDate(policy.endDate),
      premiumPrice: formattedPremium,
      previousClaim: policy.isClaimed ? 'Yes' : 'No',
      paymentLink: paymentLink || policy.paymentLink,
      additionalNotice: additionalNotice || policy.additionalNotice,
      hasRenewalNoticePdf: !!policy.renewalNoticeUrl,
      baseUrl: appConfig.apiBaseUrl.replace(/\/+$/, ''),
      policyId: policy.id,
    });
  }

  function sendWhatsApp() {
    if (!tel) {
      toast.error('No phone number on file');
      return;
    }

    const errors: { premiumPrice?: string; paymentLink?: string } = {};
    if (premiumPrice && !/^\d+(\.\d{1,2})?$/.test(premiumPrice)) {
      errors.premiumPrice = 'Enter a valid amount (e.g. 15000)';
    }
    if (paymentLink && !VALIDATION.URL.test(paymentLink)) {
      errors.paymentLink = VALIDATION_ERRORS.URL;
    }
    setWaErrors(errors);
    if (Object.keys(errors).length > 0) return;

    window.open(
      `https://wa.me/${tel.replace('+', '')}?text=${encodeURIComponent(buildMessage())}`,
      '_blank',
    );
    setShowWAModal(false);
  }

  function handleStatusClick(s: RenewalStatus) {
    if (s === policy.renewalStatus) {
      if (s === RenewalStatus.REMINDED || s === RenewalStatus.NOT_RENEWED) {
        const defaultDate = policy.nextReminderDate
          ? new Date(policy.nextReminderDate).toISOString().slice(0, 10)
          : new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
        setNextReminderDate(defaultDate);
        setTargetReminderStatus(s);
        setShowReminderModal(true);
      }
      return;
    }
    if (s === RenewalStatus.PENDING) {
      if (policy.renewalStatus !== RenewalStatus.PENDING) {
        toast.error('Policy status cannot be reverted back to Pending once updated');
        return;
      }
    }
    if (s === RenewalStatus.RENEWED) {
      const defaultLastRenewed = policy.lastRenewedAt
        ? new Date(policy.lastRenewedAt).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10);
      setRenewLastRenewedDate(defaultLastRenewed);
      const currentEnd = new Date(policy.endDate);
      const nextEnd = isNaN(currentEnd.getTime()) ? new Date() : new Date(currentEnd);
      nextEnd.setFullYear(nextEnd.getFullYear() + 1);
      setRenewNextDueDate(nextEnd.toISOString().slice(0, 10));
      setRenewPaidAmt(policy.premiumPrice ? String(policy.premiumPrice) : '');
      setShowRenewModal(true);
      return;
    }
    if (s === RenewalStatus.REMINDED || s === RenewalStatus.NOT_RENEWED) {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      setNextReminderDate(d.toISOString().slice(0, 10));
      setTargetReminderStatus(s);
      setShowReminderModal(true);
      return;
    }
    if (s === RenewalStatus.LAPSED || s === RenewalStatus.INACTIVE) {
      setTargetArchiveStatus(s);
      setShowArchiveConfirm(true);
      return;
    }
    updateStatus.mutate({ id, status: s });
  }

  function handleConfirmRenew() {
    if (!renewLastRenewedDate) {
      toast.error('Last renewed date is required');
      return;
    }
    if (!renewNextDueDate) {
      toast.error('Next expiry date is required');
      return;
    }
    const currDate = new Date(policy.endDate).getTime();
    const newDate = new Date(renewNextDueDate).getTime();
    if (isNaN(newDate)) {
      toast.error('Invalid next expiry date');
      return;
    }
    if (newDate <= currDate) {
      toast.error('Next expiry date must be after current policy end date');
      return;
    }

    const payload: Record<string, unknown> = {
      renewalStatus: RenewalStatus.RENEWED,
      endDate: renewNextDueDate,
      lastRenewedAt: renewLastRenewedDate,
      nextReminderDate: null,
    };
    if (renewPaidAmt && !isNaN(Number(renewPaidAmt))) {
      payload.premiumPrice = Number(renewPaidAmt);
    }
    updatePolicy.mutate(
      { id: policy.id, data: payload },
      {
        onSuccess: () => {
          setShowRenewModal(false);
        },
      },
    );
  }

  function handleConfirmReminder() {
    if (!targetReminderStatus) return;
    if (!nextReminderDate) {
      toast.error('Next reminder date is required');
      return;
    }
    const todayStr = new Date().toISOString().slice(0, 10);
    if (nextReminderDate < todayStr) {
      toast.error('Next reminder date cannot be in the past');
      return;
    }

    updatePolicy.mutate(
      {
        id: policy.id,
        data: {
          nextReminderDate,
          renewalStatus: targetReminderStatus,
        },
      },
      {
        onSuccess: () => {
          setShowReminderModal(false);
          setTargetReminderStatus(null);
        },
      },
    );
  }

  function handleConfirmArchive() {
    if (!targetArchiveStatus) return;
    updatePolicy.mutate(
      { id: policy.id, data: { renewalStatus: targetArchiveStatus, nextReminderDate: null } },
      {
        onSuccess: () => {
          setShowArchiveConfirm(false);
          setTargetArchiveStatus(null);
        },
      },
    );
  }

  function handleDelete() {
    deletePolicy.mutate(id, {
      onSuccess: () => {
        history.replace('/policies');
      },
    });
  }

  const isDeleting = deletePolicy.isPending;
  const messagePreview = buildMessage();

  const urgencyBannerClass = {
    overdue: 'border-rose-500/15 bg-rose-500/[0.04]',
    due7: 'border-amber-500/15 bg-amber-500/[0.04]',
    due30: 'border-emerald-500/15 bg-emerald-500/[0.04]',
    future: 'border-slate-500/15 bg-slate-500/[0.04]',
  }[urgency];

  const urgencyGradient = {
    overdue: 'from-rose-500/10 via-rose-500/[0.02] to-transparent',
    due7: 'from-amber-500/10 via-amber-500/[0.02] to-transparent',
    due30: 'from-emerald-500/10 via-emerald-500/[0.02] to-transparent',
    future: 'from-slate-500/10 via-slate-500/[0.02] to-transparent',
  }[urgency];

  const avatarGradient = {
    overdue: 'from-rose-500 to-red-600 text-white shadow-rose-500/20',
    due7: 'from-amber-500 to-yellow-600 text-white shadow-amber-500/20',
    due30: 'from-emerald-500 to-teal-600 text-white shadow-emerald-500/20',
    future: 'from-slate-500 to-slate-600 text-white shadow-slate-500/20',
  }[urgency];

  const badgeStyle = {
    overdue:
      'bg-rose-500/10 text-rose-700 border-rose-500/20 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-900/50',
    due7: 'bg-amber-500/10 text-amber-700 border-amber-500/20 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-900/50',
    due30:
      'bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-900/50',
    future:
      'bg-slate-500/10 text-slate-700 border-slate-500/20 dark:text-slate-300 dark:bg-slate-950/40 dark:border-slate-900/50',
  }[urgency];

  const ambientGlowColor = {
    overdue: 'bg-rose-500/10',
    due7: 'bg-amber-500/10',
    due30: 'bg-emerald-500/10',
    future: 'bg-slate-500/10',
  }[urgency];

  const StatusIcon = {
    overdue: AlertCircle,
    due7: Clock,
    due30: CheckCircle2,
    future: CheckCircle2,
  }[urgency];

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar
          className={cn(
            'ion-no-padding border-b relative overflow-hidden backdrop-blur-md',
            urgencyBannerClass,
            urgencyGradient,
          )}
        >
          {/* Ambient Background Light Glow */}
          <div
            className={cn(
              'absolute -top-12 -left-12 w-64 h-64 rounded-full blur-3xl opacity-60 pointer-events-none',
              ambientGlowColor,
            )}
          />

          <div className="max-w-7xl mx-auto relative z-10 px-4 sm:px-6 lg:px-8 pt-5 pb-4 sm:pt-5 sm:pb-6">
            {/* Breadcrumb — desktop (lg+) */}
            <nav className="hidden lg:flex items-center gap-2 text-xs font-semibold text-ink-muted mb-3">
              <button
                type="button"
                onClick={() => {
                  history.push('/policies');
                }}
                className="hover:text-ink transition-colors cursor-pointer"
              >
                Renewals
              </button>
              <span className="text-ink-faint">/</span>
              <span className="text-ink font-bold truncate max-w-xs">
                {policy.insuredPersonName || policy.client.insuredName}
              </span>
            </nav>

            {/* Mobile/Compact Top Action Bar (< lg) */}
            <div className="flex lg:hidden items-center justify-between w-full mb-4">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5 h-9"
                onClick={() => {
                  history.goBack();
                }}
              >
                <ArrowLeft size={14} />
                <span>Back</span>
              </Button>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5 h-9"
                  onClick={() => {
                    history.push(`/policies/${id}/edit`, { from: location.pathname });
                  }}
                  aria-label="Edit policy"
                >
                  <Pencil size={12} className="text-slate" />
                  <span>Edit</span>
                </Button>

                <Button
                  variant="destructive"
                  size="sm"
                  className="flex items-center gap-1.5 h-9"
                  onClick={() => {
                    setConfirmDelete(true);
                  }}
                  aria-label="Delete policy"
                >
                  <Trash2 size={12} />
                  <span>Delete</span>
                </Button>
              </div>
            </div>

            {/* Main Header Layout */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex items-start gap-3.5 sm:gap-4.5 min-w-0">
                {/* Desktop-only Back button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="!hidden lg:!flex items-center gap-1.5 mt-0.5 shrink-0"
                  onClick={() => {
                    history.goBack();
                  }}
                >
                  <ArrowLeft size={14} />
                  <span>Back</span>
                </Button>

                {/* Avatar + Title Container */}
                <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    className={cn(
                      'flex h-13 w-13 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr font-black text-base sm:text-lg shadow-md select-none border border-white/20 transition-all mt-0.5',
                      avatarGradient,
                    )}
                  >
                    {initials(policy.insuredPersonName || policy.client.insuredName)}
                  </motion.div>

                  <div className="text-left min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="text-lg sm:text-xl lg:text-2xl font-black text-ink leading-tight tracking-tight break-words">
                        {policy.insuredPersonName || policy.client.insuredName}
                      </h1>
                      {(policy.referenceName || policy.client?.referenceName) && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-teal-500/10 dark:bg-teal-400/15 text-teal-800 dark:text-teal-200 border border-teal-500/30 dark:border-teal-400/40 text-xs font-black shadow-2xs">
                          <Tag size={11} className="text-teal-600 dark:text-teal-400 shrink-0" />
                          <span>{policy.referenceName ?? policy.client?.referenceName}</span>
                        </span>
                      )}
                    </div>

                    {policy.insuredPersonName && (
                      <p className="text-xs text-ink-faint font-semibold mt-1">
                        Client Contact: {policy.client.insuredName} ({policy.client.mobileNumber})
                      </p>
                    )}

                    {/* Structured Info Badges */}
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-2.5">
                      {/* Urgency Status Badge */}
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border backdrop-blur-sm shadow-2xs',
                          badgeStyle,
                        )}
                      >
                        <StatusIcon size={12} className="shrink-0" />
                        {URGENCY_LABELS[urgency]}
                      </span>

                      {/* Time Left Badge */}
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border border-line bg-surface/70 text-ink-soft backdrop-blur-sm shadow-2xs">
                        <Clock size={12} className="shrink-0 text-slate" />
                        {daysLabel(days)}
                      </span>

                      {/* Expiry Date Badge */}
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border border-line bg-surface/70 text-ink-soft backdrop-blur-sm shadow-2xs">
                        <Calendar size={12} className="shrink-0 text-slate" />
                        Expires {formatDate(policy.endDate)}
                      </span>

                      {/* Associate Policy Badge */}
                      {policy.insuredPersonName && policy.client?.insuredName && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border border-sky-500/25 bg-sky-500/10 text-sky-700 dark:text-sky-300 backdrop-blur-sm shadow-2xs">
                          <Users size={12} className="shrink-0 text-sky-500" />
                          Client: {policy.client.insuredName}
                        </span>
                      )}

                      {/* Outsourced Policy Badge */}
                      {(policy.isOutsourced || policy.associateAgent) && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border border-purple-500/25 bg-purple-500/10 text-purple-700 dark:text-purple-300 backdrop-blur-sm shadow-2xs">
                          <UserCheck size={12} className="shrink-0 text-purple-500" />
                          Outsourced{policy.associateAgent ? `: ${policy.associateAgent.name}` : ''}
                        </span>
                      )}

                      {/* Insurance Provider Badge */}
                      {policy.insuranceProvider && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border border-indigo-500/25 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 backdrop-blur-sm shadow-2xs">
                          <Building2 size={12} className="shrink-0 text-indigo-500" />
                          {policy.insuranceProvider.name}
                        </span>
                      )}

                      {/* Vehicle Number or Pending Badge */}
                      {policy.vehicleNumber ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-mono font-bold uppercase tracking-wider border border-line bg-surface/70 text-ink backdrop-blur-sm shadow-2xs">
                          <Car size={12} className="shrink-0 text-slate" />
                          {policy.vehicleNumber}
                        </span>
                      ) : isMotorPolicy(policy.policyType.name) ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border border-amber-500/35 bg-amber-500/10 text-amber-700 dark:text-amber-300 backdrop-blur-sm shadow-2xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                          Vehicle No : Pending
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop-only Action buttons (lg+) */}
              <div className="hidden lg:flex gap-2.5 shrink-0 justify-end mt-1">
                <Button
                  variant="outline"
                  size="md"
                  className="flex items-center gap-2 h-10 px-4 rounded-xl border-line bg-surface hover:bg-paper text-ink font-bold shadow-xs transition-all cursor-pointer"
                  onClick={() => {
                    history.push(`/policies/${id}/edit`, { from: location.pathname });
                  }}
                  aria-label="Edit policy"
                >
                  <Pencil size={13} className="text-slate" />
                  <span>Edit Details</span>
                </Button>

                <Button
                  variant="destructive"
                  size="md"
                  className="flex items-center gap-2 h-10 px-4 rounded-xl font-bold shadow-xs transition-all cursor-pointer"
                  onClick={() => {
                    setConfirmDelete(true);
                  }}
                  aria-label="Delete policy"
                >
                  <Trash2 size={13} />
                  <span>Delete</span>
                </Button>
              </div>
            </div>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding-bottom">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          className="min-h-full bg-body-bg"
        >
          {/* ── Content body ── */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 md:pt-6 pb-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
              {/* ── Top Left: Client & Policy Specifications (order 1 on mobile, left on desktop) ── */}
              <div className="order-1 lg:col-span-7 xl:col-span-8 lg:col-start-1 lg:row-start-1 space-y-6">
                {/* ─── Client Identity Card ─── */}
                <section className="bg-surface border border-line rounded-2xl p-5 sm:p-6 shadow-xs text-left space-y-5">
                  <div className="flex items-center justify-between pb-3.5 border-b border-line">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-950/45 text-indigo-700 dark:text-indigo-300 border border-indigo-200/40 shrink-0">
                        <User size={16} />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-ink leading-tight">
                          Client &amp; Insured Identity
                        </h3>
                        <p className="text-[11px] text-ink-faint">
                          Policy client registry &amp; contact details
                        </p>
                      </div>
                    </div>
                    {policy.insuredPersonName ? (
                      <Badge tone="pending">Associate Policy</Badge>
                    ) : (
                      <Badge tone="neutral">Direct / Self</Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <SpecField
                      label="Insured Person"
                      value={policy.insuredPersonName || policy.client.insuredName}
                      icon={<User size={12} className="text-indigo-500" />}
                    />
                    <SpecField
                      label="Contact Client Profile"
                      value={policy.client.insuredName}
                      icon={<Users size={12} className="text-sky-500" />}
                    />
                    <SpecField
                      label="Mobile Number"
                      value={
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-ink">
                            {policy.client.mobileNumber ?? 'None'}
                          </span>
                          {policy.client.mobileNumber && (
                            <button
                              type="button"
                              onClick={() => {
                                void navigator.clipboard.writeText(policy.client.mobileNumber!);
                                toast.success('Mobile number copied');
                              }}
                              className="p-1 rounded bg-surface border border-line text-ink-soft hover:text-ink transition-colors cursor-pointer"
                              title="Copy mobile number"
                            >
                              <Copy size={11} />
                            </button>
                          )}
                        </div>
                      }
                      icon={<Phone size={12} className="text-emerald-500" />}
                    />
                    <SpecField
                      label="Reference Profile"
                      value={
                        policy.referenceName || policy.client?.referenceName ? (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-teal-500/10 dark:bg-teal-400/15 text-teal-800 dark:text-teal-200 border border-teal-500/30 dark:border-teal-400/40 text-xs font-black shadow-2xs">
                              <Tag
                                size={11}
                                className="text-teal-600 dark:text-teal-400 shrink-0"
                              />
                              <span>{policy.referenceName ?? policy.client?.referenceName}</span>
                            </span>
                            {policy.referenceNote && (
                              <span className="rounded-lg bg-paper px-2 py-0.5 border border-line text-[11px] font-bold text-ink-soft">
                                {policy.referenceNote}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-ink-faint font-normal">None</span>
                        )
                      }
                      icon={<Tag size={12} className="text-teal-500" />}
                    />
                  </div>

                  {isOutsourced && associateAgentObj && (
                    <div className="p-3.5 sm:p-4 rounded-xl bg-purple-500/8 border border-purple-500/20 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-black uppercase tracking-wider text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
                          <UserCheck size={14} />
                          Associate Agent (Outsourced Partner)
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-purple-500/15 text-purple-800 dark:text-purple-200 border border-purple-500/20">
                          Outsourced
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-1">
                        <div>
                          <div className="text-sm font-bold text-ink">
                            {associateAgentObj.name}{' '}
                            {associateAgentObj.agencyName
                              ? `(${associateAgentObj.agencyName})`
                              : ''}
                          </div>
                          <div className="text-xs font-mono font-semibold text-purple-700 dark:text-purple-300 mt-0.5">
                            Mobile: {associateAgentObj.mobileNumber}
                          </div>
                          {associateAgentObj.notes && (
                            <p className="text-[11px] text-ink-muted italic mt-1">
                              "{associateAgentObj.notes}"
                            </p>
                          )}
                        </div>
                        <p className="text-[10px] text-purple-600 dark:text-purple-400 font-semibold sm:max-w-[200px] text-left sm:text-right">
                          * Automated &amp; manual reminders will target this partner agent.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Communication Action Strip */}
                  <div className="pt-3.5 border-t border-line">
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-xs font-bold text-ink-faint uppercase tracking-wider">
                        Quick Contact Outreach
                      </span>
                      <span className="text-[11px] text-ink-soft">
                        Target:{' '}
                        {tel ? (
                          <span className="font-mono font-bold text-ink">{tel}</span>
                        ) : (
                          <span className="text-ink-faint">No phone</span>
                        )}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      <CommButton
                        icon={<Phone size={15} />}
                        label="Call Client"
                        disabled={!tel}
                        className="text-blue-700 bg-blue-50/80 border-blue-200/80 hover:bg-blue-100 dark:text-blue-300 dark:bg-blue-950/40 dark:border-blue-900/50"
                        onClick={callAgent}
                      />
                      <CommButton
                        icon={<MessageCircle size={15} />}
                        label="WhatsApp"
                        disabled={!tel}
                        className="text-green-700 bg-green-50/80 border-green-200/80 hover:bg-green-100 dark:text-green-300 dark:bg-green-950/40 dark:border-green-900/50"
                        onClick={() => {
                          setPremiumPrice(String(policy.premiumPrice ?? ''));
                          setPaymentLink(policy.paymentLink ?? '');
                          setAdditionalNotice(policy.additionalNotice ?? '');
                          setWaErrors({});
                          setShowWAModal(true);
                        }}
                      />
                      <CommButton
                        icon={<Send size={15} />}
                        label="SMS Notice"
                        disabled={!tel}
                        className="text-amber-700 bg-amber-50/80 border-amber-200/80 hover:bg-amber-100 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-900/50"
                        onClick={smsAgent}
                      />
                    </div>
                  </div>
                </section>

                {/* ─── Policy Specifications Card ─── */}
                <section className="bg-surface border border-line rounded-2xl p-5 sm:p-6 shadow-xs text-left space-y-5">
                  <div className="flex items-center justify-between pb-3.5 border-b border-line">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate/10 dark:bg-slate/20 text-slate border border-slate/15 shrink-0">
                        <CreditCard size={16} />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-ink leading-tight">
                          Policy Specifications
                        </h3>
                        <p className="text-[11px] text-ink-faint">
                          Insurance term, coverage &amp; financial specifications
                        </p>
                      </div>
                    </div>
                    {policy.premiumPrice != null && (
                      <span className="text-sm font-black text-ink bg-paper px-3 py-1 rounded-xl border border-line shadow-2xs">
                        ₹{policy.premiumPrice.toLocaleString('en-IN')}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <SpecField
                      label="Insurance Type"
                      value={
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg bg-paper border border-line text-xs font-bold text-ink">
                          {policy.policyType.name}
                        </span>
                      }
                      icon={<ShieldCheck size={12} className="text-slate" />}
                    />
                    <SpecField
                      label="Provider / Agency"
                      value={
                        policy.insuranceProvider ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/45 text-indigo-700 dark:text-indigo-300 border border-indigo-200/40 text-xs font-bold">
                            <Building2 size={12} className="shrink-0" />
                            {policy.insuranceProvider.name}
                          </span>
                        ) : (
                          <span className="text-xs text-ink-faint font-normal">Not specified</span>
                        )
                      }
                      icon={<Building2 size={12} className="text-indigo-500" />}
                    />
                    <SpecField
                      label="Policy Number"
                      value={
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-ink">
                            {policy.policyNumber || 'None'}
                          </span>
                          {policy.policyNumber && (
                            <button
                              type="button"
                              onClick={() => {
                                void navigator.clipboard.writeText(policy.policyNumber!);
                                toast.success('Policy number copied');
                              }}
                              className="p-1 rounded bg-surface border border-line text-ink-soft hover:text-ink transition-colors cursor-pointer"
                              title="Copy policy number"
                            >
                              <Copy size={11} />
                            </button>
                          )}
                        </div>
                      }
                      icon={<CreditCard size={12} className="text-slate" />}
                    />
                    <SpecField
                      label="Vehicle Registration"
                      value={
                        policy.vehicleNumber ? (
                          <span className="inline-flex items-center gap-1.5 font-mono uppercase text-xs bg-paper px-2.5 py-1 rounded-lg border border-line font-bold text-ink tracking-wider shadow-2xs">
                            <Car size={13} className="text-slate shrink-0" />
                            {policy.vehicleNumber}
                          </span>
                        ) : isMotorPolicy(policy.policyType.name) ? (
                          <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 bg-amber-500/10 dark:bg-amber-950/40 px-2.5 py-1 rounded-lg border border-amber-500/20 font-semibold">
                            <AlertCircle
                              size={13}
                              className="shrink-0 text-amber-600 dark:text-amber-400"
                            />
                            (New Vehicle / Pending)
                          </span>
                        ) : (
                          <span className="text-xs text-ink-faint font-normal">
                            N/A (Non-Motor)
                          </span>
                        )
                      }
                      icon={<Car size={12} className="text-slate" />}
                    />
                    <SpecField
                      label="Renewal Expiry Date"
                      value={
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-xs sm:text-sm">
                            {formatDate(policy.endDate)}
                          </span>
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border',
                              badgeStyle,
                            )}
                          >
                            {daysLabel(days)}
                          </span>
                        </div>
                      }
                      icon={<Calendar size={12} className="text-rose-500" />}
                    />
                    <SpecField
                      label="Last Renewed Date"
                      value={
                        policy.lastRenewedAt ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 border border-emerald-500/20 text-xs font-bold">
                            <Check
                              size={12}
                              className="text-emerald-600 dark:text-emerald-400 shrink-0"
                            />
                            {formatDate(policy.lastRenewedAt)}
                          </span>
                        ) : (
                          <span className="text-xs text-ink-faint font-normal">Never recorded</span>
                        )
                      }
                      icon={<Clock size={12} className="text-emerald-500" />}
                    />
                    {(policy.renewalStatus === 'REMINDED' ||
                      policy.renewalStatus === 'NOT_RENEWED') && (
                      <SpecField
                        label="Next Remind Date"
                        value={
                          policy.nextReminderDate ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-amber-500/10 text-amber-900 dark:text-amber-200 border border-amber-500/30 text-xs font-black shadow-2xs">
                                <Bell
                                  size={12}
                                  className="text-amber-600 dark:text-amber-400 shrink-0"
                                />
                                {formatDate(policy.nextReminderDate)}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  const d = new Date(policy.nextReminderDate!);
                                  setNextReminderDate(d.toISOString().slice(0, 10));
                                  setTargetReminderStatus(policy.renewalStatus);
                                  setShowReminderModal(true);
                                }}
                                className="text-[11px] font-bold text-slate hover:underline cursor-pointer"
                              >
                                Change
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                const d = new Date();
                                d.setDate(d.getDate() + 7);
                                setNextReminderDate(d.toISOString().slice(0, 10));
                                setTargetReminderStatus(policy.renewalStatus);
                                setShowReminderModal(true);
                              }}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-500/30 text-xs font-bold hover:bg-amber-500/20 transition cursor-pointer shadow-2xs"
                            >
                              <Bell
                                size={13}
                                className="text-amber-600 dark:text-amber-400 shrink-0 animate-pulse"
                              />
                              <span>Set Remind Date</span>
                            </button>
                          )
                        }
                        icon={<Bell size={12} className="text-amber-500" />}
                      />
                    )}
                    <SpecField
                      label="Payment Link"
                      value={
                        policy.paymentLink ? (
                          <div className="flex items-center gap-1.5 min-w-0 max-w-full">
                            <a
                              href={policy.paymentLink}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-1 text-slate hover:underline font-mono text-xs min-w-0 truncate"
                              title={policy.paymentLink}
                            >
                              <ExternalLink size={12} className="shrink-0" />
                              <span className="truncate">{policy.paymentLink}</span>
                            </a>
                            <button
                              onClick={handleCopyLink}
                              className="p-1 rounded bg-paper border border-line text-ink-soft hover:text-ink transition-all cursor-pointer shrink-0"
                              title="Copy link"
                            >
                              {copied ? (
                                <Check size={11} className="text-green-fg" />
                              ) : (
                                <Copy size={11} />
                              )}
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-ink-faint font-normal">
                            None configured
                          </span>
                        )
                      }
                      icon={<ExternalLink size={12} className="text-slate" />}
                    />
                    <SpecField
                      label="Renewal Notice PDF"
                      value={
                        policy.renewalNoticeUrl ? (
                          <div className="flex items-center justify-between gap-2">
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink">
                              <FileText size={13} className="text-rose-500 shrink-0" />
                              Notice PDF attached
                            </span>
                            <button
                              type="button"
                              onClick={handleViewPdf}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-slate/10 text-slate hover:bg-slate/20 text-xs font-bold transition-colors cursor-pointer"
                            >
                              View PDF
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-ink-faint font-normal">None attached</span>
                        )
                      }
                      icon={<FileText size={12} className="text-rose-500" />}
                    />
                    <SpecField
                      label="Claim Status"
                      value={
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] border',
                              policy.isClaimed
                                ? 'bg-rose-500/10 text-rose-700 border-rose-500/20 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-900/50'
                                : 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-900/50',
                            )}
                          >
                            {policy.isClaimed ? 'Claimed' : 'No Claim'}
                          </span>
                          {policy.isClaimed && policy.claimAmount != null && (
                            <span className="text-xs font-bold text-ink">
                              ₹{policy.claimAmount.toLocaleString('en-IN')}
                            </span>
                          )}
                          {policy.isClaimed && policy.claimDate && (
                            <span className="text-[11px] text-ink-faint">
                              ({formatDate(policy.claimDate)})
                            </span>
                          )}
                        </div>
                      }
                      icon={<Shield size={12} className="text-amber-500" />}
                    />
                  </div>
                </section>
              </div>

              {/* ── Right Column: Sticky Lifecycle & Outreach Sidebar (order 2 on mobile, right on desktop) ── */}
              <div className="order-2 lg:col-span-5 xl:col-span-4 lg:col-start-8 xl:lg:col-start-9 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-6 space-y-6">
                {/* ─── Manage Renewal Status ─── */}
                <section className="bg-surface border border-line rounded-2xl p-5 sm:p-6 shadow-xs text-left space-y-4">
                  <div className="flex items-center justify-between pb-3.5 border-b border-line">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate/10 dark:bg-slate/20 text-slate border border-slate/15 shrink-0">
                        <Clock size={16} />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-ink leading-tight">
                          Manage Renewal Status
                        </h3>
                        <p className="text-[11px] text-ink-faint">
                          Select state to update policy lifecycle
                        </p>
                      </div>
                    </div>
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  </div>

                  {/* Active Status Highlight Banner */}
                  <div className="p-3.5 rounded-xl bg-paper border border-line flex items-center justify-between gap-3">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-ink-faint block">
                        Current Status
                      </span>
                      <span className="text-sm font-extrabold text-ink mt-0.5 flex items-center gap-1.5">
                        <span
                          className={cn(
                            'w-2.5 h-2.5 rounded-full shrink-0',
                            statusMeta[policy.renewalStatus]?.dot ?? 'bg-slate',
                          )}
                        />
                        {RENEWAL_STATUS_LABELS[policy.renewalStatus] ?? policy.renewalStatus}
                      </span>
                    </div>
                    <span
                      className={cn('text-xs font-bold px-2.5 py-1 rounded-lg border', badgeStyle)}
                    >
                      {daysLabel(days)}
                    </span>
                  </div>

                  {/* Status Buttons Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
                    {ALL_STATUSES.map((s) => {
                      const active = policy.renewalStatus === s;
                      const meta = statusMeta[s];
                      return (
                        <motion.button
                          key={s}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            handleStatusClick(s);
                          }}
                          className={cn(
                            'relative flex items-center gap-3 rounded-xl border px-3.5 py-2.5 text-xs sm:text-sm font-semibold transition-all cursor-pointer text-left',
                            active
                              ? 'border-slate bg-slate/8 dark:bg-slate/12 text-ink shadow-xs font-bold'
                              : 'border-line bg-paper/50 text-ink-soft hover:bg-paper hover:border-line-strong hover:text-ink',
                          )}
                        >
                          <span
                            className={cn(
                              'w-2.5 h-2.5 rounded-full shrink-0 transition-all',
                              active ? meta.dot : 'bg-line-strong',
                            )}
                          />
                          <span className="flex-1 truncate">{RENEWAL_STATUS_LABELS[s] ?? s}</span>
                          {active && (
                            <motion.span
                              initial={{ scale: 0, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              className="shrink-0"
                            >
                              <CheckCircle2 size={16} className="text-slate" />
                            </motion.span>
                          )}
                          {active && (
                            <motion.span
                              layoutId="status-active-bar"
                              className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-slate"
                              initial={{ opacity: 0, scaleY: 0.5 }}
                              animate={{ opacity: 1, scaleY: 1 }}
                              exit={{ opacity: 0, scaleY: 0.5 }}
                              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                            />
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                </section>

                {/* ─── Notice Readiness & Outreach Hub ─── */}
                <section className="bg-surface border border-line rounded-2xl p-5 shadow-xs text-left space-y-4">
                  <div className="flex items-center gap-2.5 pb-3 border-b border-line">
                    <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20 shrink-0">
                      <MessageCircle size={16} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-ink leading-tight">Notice Readiness</h3>
                      <p className="text-[11px] text-ink-faint">
                        Outreach checklist &amp; composer
                      </p>
                    </div>
                  </div>

                  {/* Checklist */}
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center justify-between py-1 border-b border-line/60">
                      <span className="text-ink-soft">Target Phone</span>
                      {tel ? (
                        <span className="font-mono font-bold text-green-600 dark:text-green-400 flex items-center gap-1">
                          <Check size={12} /> Ready
                        </span>
                      ) : (
                        <span className="text-rose-500 font-bold flex items-center gap-1">
                          <AlertCircle size={12} /> Missing
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between py-1 border-b border-line/60">
                      <span className="text-ink-soft">Premium Quote</span>
                      {policy.premiumPrice != null ? (
                        <span className="font-bold text-green-600 dark:text-green-400 flex items-center gap-1">
                          <Check size={12} /> ₹{policy.premiumPrice.toLocaleString('en-IN')}
                        </span>
                      ) : (
                        <span className="text-amber-600 font-bold flex items-center gap-1">
                          <AlertCircle size={12} /> Not set
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between py-1 border-b border-line/60">
                      <span className="text-ink-soft">Payment Link</span>
                      {policy.paymentLink ? (
                        <span className="font-bold text-green-600 dark:text-green-400 flex items-center gap-1">
                          <Check size={12} /> Configured
                        </span>
                      ) : (
                        <span className="text-ink-faint font-medium">Optional</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <span className="text-ink-soft">Renewal Notice PDF</span>
                      {policy.renewalNoticeUrl ? (
                        <span className="font-bold text-green-600 dark:text-green-400 flex items-center gap-1">
                          <Check size={12} /> Attached
                        </span>
                      ) : (
                        <span className="text-ink-faint font-medium">None</span>
                      )}
                    </div>
                  </div>

                  <Button
                    variant="primary"
                    size="md"
                    className="w-full bg-[#25D366] hover:bg-[#1ea855] text-white shadow-xs border-transparent font-bold flex items-center justify-center gap-2 cursor-pointer"
                    disabled={!tel}
                    onClick={() => {
                      setPremiumPrice(String(policy.premiumPrice ?? ''));
                      setPaymentLink(policy.paymentLink ?? '');
                      setAdditionalNotice(policy.additionalNotice ?? '');
                      setWaErrors({});
                      setShowWAModal(true);
                    }}
                  >
                    <MessageCircle size={15} />
                    <span>Compose WhatsApp Notice</span>
                  </Button>
                </section>
              </div>

              {/* ── Bottom Left: Claims, Documents & History (order 3 on mobile, left on desktop) ── */}
              <div className="order-3 lg:col-span-7 xl:col-span-8 lg:col-start-1 lg:row-start-2 space-y-6">
                {/* Policy Claims Tracking Section */}
                <PolicyClaimsSection policyId={policy.id} />

                {/* Policy Documents (Yearly PDFs) */}
                <PolicyDocumentsSection
                  policyId={policy.id}
                  defaultYear={new Date(policy.endDate).getFullYear()}
                />

                {/* Status Transition History Timeline */}
                <StatusHistorySection history={historyData} />
              </div>
            </div>
          </div>
        </motion.div>
      </IonContent>

      {/* ── WhatsApp Composer Dialog ── */}
      <Dialog
        open={showWAModal}
        onClose={() => {
          setShowWAModal(false);
          setWaErrors({});
        }}
        title="WhatsApp Composer"
        description="Customize the message before sending."
        sheet
      >
        <div className="space-y-4">
          <Input
            label="Premium Price (Rs.)"
            placeholder="e.g. 12500"
            type="number"
            value={premiumPrice}
            onChange={(e) => {
              setPremiumPrice(e.target.value);
              if (waErrors.premiumPrice)
                setWaErrors((p) => {
                  const n = { ...p };
                  delete n.premiumPrice;
                  return n;
                });
            }}
            error={waErrors.premiumPrice}
          />
          <Input
            label="Payment Link"
            placeholder="https://pay.insurer.com/..."
            value={paymentLink}
            onChange={(e) => {
              setPaymentLink(e.target.value);
              if (waErrors.paymentLink)
                setWaErrors((p) => {
                  const n = { ...p };
                  delete n.paymentLink;
                  return n;
                });
            }}
            error={waErrors.paymentLink}
          />
          {/* Renewal Notice PDF Indicator / Upload Field */}
          <div className="flex flex-col gap-2 text-left">
            <label className="text-xs font-bold text-ink-soft">Renewal Notice PDF</label>
            <input
              ref={pdfInputRef}
              type="file"
              accept=".pdf"
              onChange={handlePdfUpload}
              className="hidden"
              disabled={updatePolicy.isPending}
            />
            {policy.renewalNoticeUrl ? (
              <div className="flex items-center justify-between gap-2 rounded-xl border border-line bg-paper/60 px-3 py-2.5">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileText size={15} className="shrink-0 text-rose-500" />
                  <span className="text-xs font-semibold text-ink truncate flex-1">
                    Renewal notice PDF attached
                  </span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={handleViewPdf}
                    className="text-xs font-bold text-slate hover:underline px-1.5 py-1 cursor-pointer"
                  >
                    View
                  </button>
                  <button
                    type="button"
                    disabled={updatePolicy.isPending}
                    onClick={handleRemovePdf}
                    className="p-1 rounded-lg hover:bg-line-strong/30 transition-colors cursor-pointer shrink-0 disabled:opacity-50"
                    title="Remove PDF"
                  >
                    <X size={14} className="text-ink-faint" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                disabled={updatePolicy.isPending}
                onClick={() => pdfInputRef.current?.click()}
                className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-paper/40 px-4 py-3 text-xs font-semibold text-ink-soft hover:border-slate hover:text-ink transition-colors cursor-pointer disabled:opacity-50"
              >
                {updatePolicy.isPending ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4 text-ink-soft animate-infinite"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Uploading PDF...
                  </span>
                ) : (
                  <>
                    <Paperclip size={14} />
                    Upload PDF
                  </>
                )}
              </button>
            )}
          </div>

          <div>
            <Input
              label="Additional Notice Lines"
              placeholder="e.g. Please carry original RC book"
              value={additionalNotice}
              onChange={(e) => {
                if (e.target.value.length <= 1000) setAdditionalNotice(e.target.value);
              }}
            />
            <div className="flex justify-end mt-1">
              <span className="text-[10px] text-ink-faint">{additionalNotice.length}/1000</span>
            </div>
          </div>

          {/* ── Realistic WhatsApp Chat Preview ── */}
          <div className="rounded-xl border border-line bg-paper shadow-sm overflow-hidden">
            {/* Chat header bar */}
            <div className="flex items-center gap-2 bg-[#075e54] px-3 py-2">
              <div className="size-6 rounded-full bg-[#25D366] flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="size-3.5 text-white fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-white leading-tight truncate">
                  Renewal Reminder
                </p>
                <p className="text-[9px] text-white/70">
                  Agent •{' '}
                  {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <svg viewBox="0 0 24 24" className="size-4 text-white/60 fill-current">
                <path d="M12 7a2 2 0 110-4 2 2 0 010 4zm0 7a2 2 0 110-4 2 2 0 010 4zm0 7a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </div>
            {/* Message bubble */}
            <div className="px-3 py-3">
              <div className="relative max-w-[88%] bg-[#dcf8c6] rounded-lg rounded-bl-none px-3 py-2.5 shadow-sm">
                {/* Tail */}
                <div className="absolute -left-[5px] bottom-0 w-0 h-0 border-l-[6px] border-l-transparent border-b-[10px] border-b-[#dcf8c6] border-r-[6px] border-r-transparent" />
                <p className="text-xs leading-relaxed text-gray-800 whitespace-pre-wrap">
                  {messagePreview.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
                    /^https?:\/\//.test(part) ? (
                      <a
                        key={i}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#039be5] underline font-medium break-all"
                      >
                        {part}
                      </a>
                    ) : (
                      <span key={i}>{part}</span>
                    ),
                  )}
                </p>
                {/* Timestamp */}
                <p className="text-[9px] text-gray-500 text-right mt-1 select-none">
                  {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}{' '}
                  <span className="inline-block align-middle">
                    <svg viewBox="0 0 16 11" className="size-3 fill-current text-gray-500">
                      <path d="M11.071.653a.457.457 0 00-.304-.102.493.493 0 00-.381.178l-6.19 7.636-2.011-2.095a.463.463 0 00-.304-.153.464.464 0 00-.33.102.495.495 0 00-.178.38.5.5 0 00.153.331L4.234 9.65c.102.102.228.153.33.153a.48.48 0 0 0 .33-.153l6.43-7.92a.45.45 0 0 0 .102-.33.48.48 0 0 0-.355-.746Z" />
                    </svg>
                  </span>
                </p>
              </div>
            </div>
          </div>

          <Button
            variant="primary"
            size="lg"
            className="w-full bg-[#25D366] hover:bg-[#1ea855] shadow-[0_4px_16px_rgba(37,211,102,0.30)] border-transparent"
            leftIcon={<MessageCircle size={16} />}
            onClick={sendWhatsApp}
          >
            Send via WhatsApp
          </Button>
        </div>
      </Dialog>

      {/* ── Renew Policy Modal (Image 2) ── */}
      <Dialog
        open={showRenewModal}
        onClose={() => setShowRenewModal(false)}
        title="Renew Policy"
        description="Set the last renewed date, next expiration date, and premium details for the renewed policy."
        sheet
      >
        <div className="space-y-4 text-left">
          <Input
            label="Last Renewed Date *"
            type="date"
            value={renewLastRenewedDate}
            onChange={(e) => setRenewLastRenewedDate(e.target.value)}
            helperText="The date when the renewal was completed"
          />

          <Input
            label="Next Expiry Date (1 Year Default) *"
            type="date"
            value={renewNextDueDate}
            onChange={(e) => setRenewNextDueDate(e.target.value)}
            helperText="Policy expiration date for the new term"
          />

          <Input
            label="Paid Amount (Rs.)"
            placeholder="e.g. 15000"
            type="number"
            value={renewPaidAmt}
            onChange={(e) => setRenewPaidAmt(e.target.value)}
          />

          <div className="pt-2 flex gap-3">
            <Button
              variant="outline"
              size="lg"
              className="flex-1"
              onClick={() => setShowRenewModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="lg"
              className="flex-1"
              onClick={handleConfirmRenew}
              loading={updatePolicy.isPending}
            >
              Confirm Renewal
            </Button>
          </div>
        </div>
      </Dialog>

      {/* ── Set Reminder Modal (Image 3) ── */}
      <Dialog
        open={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        title={`Mark as ${targetReminderStatus ? RENEWAL_STATUS_LABELS[targetReminderStatus] : 'Reminded'}`}
        description="Confirm follow-up reminder date for this policy."
        sheet
      >
        <div className="space-y-4">
          <Input
            label="Next Reminder Date"
            type="date"
            value={nextReminderDate}
            onChange={(e) => setNextReminderDate(e.target.value)}
          />

          <div className="pt-2 flex gap-3">
            <Button
              variant="outline"
              size="lg"
              className="flex-1"
              onClick={() => setShowReminderModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" size="lg" className="flex-1" onClick={handleConfirmReminder}>
              Confirm Status
            </Button>
          </div>
        </div>
      </Dialog>

      {/* ── Archive Confirmation (Lapsed / Inactive - Image 3) ── */}
      <AlertDialog
        open={showArchiveConfirm}
        onClose={() => setShowArchiveConfirm(false)}
        onConfirm={handleConfirmArchive}
        title={`Mark as ${targetArchiveStatus ? RENEWAL_STATUS_LABELS[targetArchiveStatus] : 'Archived'}?`}
        description="This record will be hidden from default workbench views and can only be accessed via specific status filters."
        confirmLabel={`Yes, Set ${targetArchiveStatus ? RENEWAL_STATUS_LABELS[targetArchiveStatus] : 'Status'}`}
        cancelLabel="Cancel"
        variant="warning"
      />

      {/* ── Delete Confirmation ── */}
      <AlertDialog
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
        }}
        onConfirm={handleDelete}
        title="Delete Policy?"
        description="This will permanently remove the renewal registry card and all records. This action cannot be undone."
        confirmLabel="Yes, Delete"
        cancelLabel="Keep Policy"
        variant="destructive"
        loading={isDeleting}
        icon={<Trash2 size={20} />}
      />
      <IonFooter className="ion-no-border ion-no-padding bg-transparent md:!hidden">
        <div
          className="px-4 pt-2"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <BottomBar />
        </div>
      </IonFooter>
    </IonPage>
  );
}

/* ── Helpers ── */
function SpecField({
  label,
  value,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5 p-3 sm:p-3.5 rounded-xl bg-paper/50 border border-line/60">
      <span className="text-[11px] font-semibold text-ink-faint flex items-center gap-1.5">
        {icon}
        {label}
      </span>
      <div className="font-bold text-ink text-xs sm:text-sm min-w-0">{value}</div>
    </div>
  );
}

function CommButton({
  icon,
  label,
  onClick,
  disabled,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className: string;
}) {
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex flex-col items-center justify-center gap-1.5 py-2.5 sm:py-3 px-2 rounded-xl border font-sans text-xs font-bold shadow-2xs transition-all cursor-pointer',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        className,
      )}
    >
      {icon}
      <span>{label}</span>
    </motion.button>
  );
}

function StatusHistorySection({ history }: { history: any[] }) {
  if (!history || history.length === 0) return null;

  return (
    <section className="bg-surface border border-line rounded-2xl p-5 sm:p-6 shadow-xs text-left">
      <div className="flex items-center gap-2.5 pb-4 border-b border-line mb-6">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate/10 dark:bg-slate/20 text-slate border border-slate/15 shrink-0">
          <Calendar size={15} />
        </div>
        <div>
          <h3 className="text-sm font-bold text-ink leading-tight">Status Transition History</h3>
          <p className="text-[11px] text-ink-faint">Audit trail of renewal status updates</p>
        </div>
      </div>

      <div className="relative border-l border-line ml-3 pl-6 space-y-6">
        {history.map((item) => {
          return (
            <div key={item.id} className="relative">
              {/* Circle dot marker */}
              <span className="absolute -left-[31px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-line bg-paper shadow-2xs">
                <span className="h-1.5 w-1.5 rounded-full bg-slate" />
              </span>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-4">
                <div>
                  <div className="flex items-center flex-wrap gap-1.5 text-xs">
                    {item.previousStatus ? (
                      <>
                        <span className="font-bold text-ink-soft">{item.previousStatus}</span>
                        <span className="text-ink-faint">→</span>
                      </>
                    ) : (
                      <span className="text-ink-faint font-semibold mr-1">Created:</span>
                    )}
                    <span className="font-extrabold text-ink">{item.newStatus}</span>
                  </div>
                  {item.notes && (
                    <p className="mt-1 text-xs text-ink-soft italic font-medium">"{item.notes}"</p>
                  )}
                </div>
                <div className="text-[10px] text-ink-faint font-semibold whitespace-nowrap">
                  <span>{item.changedBy ? item.changedBy.name : 'System'}</span>
                  <span className="mx-1.5">·</span>
                  <span>{formatDate(item.changedAt)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
